import React from "react";
import { api, resolveAssetUrl } from "@/lib/api";
import { Upload, Loader2, ImagePlus } from "lucide-react";

export function TextField({ label, value, onChange, testid, placeholder, multiline = false, rows = 3 }) {
  const commonClass =
    "w-full bg-bg border border-warm rounded-lg px-4 py-3 text-cream outline-none focus:border-[color:var(--gold)] transition placeholder:text-cream/40";
  return (
    <div>
      <label className="block text-[10px] tracking-[0.35em] uppercase text-muted-warm mb-2">{label}</label>
      {multiline ? (
        <textarea
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          rows={rows}
          data-testid={testid}
          placeholder={placeholder}
          className={commonClass}
        />
      ) : (
        <input
          type="text"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
          placeholder={placeholder}
          className={commonClass}
        />
      )}
    </div>
  );
}

export function ImageField({ label, value, onChange, testid }) {
  const [uploading, setUploading] = React.useState(false);
  const [error, setError] = React.useState("");
  const inputRef = React.useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <label className="block text-[10px] tracking-[0.35em] uppercase text-muted-warm mb-2">{label}</label>
      <div className="flex items-start gap-4">
        <div className="w-28 h-28 rounded-lg overflow-hidden border border-warm bg-bg flex items-center justify-center shrink-0">
          {value ? (
            <img src={resolveAssetUrl(value)} alt="preview" className="w-full h-full object-cover" data-testid={`${testid}-preview`} />
          ) : (
            <ImagePlus size={22} className="text-cream/40" />
          )}
        </div>
        <div className="flex-1 space-y-2">
          <input
            type="text"
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            data-testid={`${testid}-url`}
            placeholder="Image URL or upload below"
            className="w-full bg-bg border border-warm rounded-lg px-4 py-2 text-cream outline-none focus:border-[color:var(--gold)] text-sm"
          />
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="hidden"
            data-testid={`${testid}-file`}
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            data-testid={`${testid}-upload-btn`}
            className="inline-flex items-center gap-2 rounded-full border border-[color:var(--gold)]/60 text-[color:var(--gold-hi)] px-4 py-2 text-xs tracking-wide uppercase hover:bg-[color:var(--gold)] hover:text-[color:var(--bg)] transition disabled:opacity-50"
          >
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            {uploading ? "Uploading..." : "Upload New"}
          </button>
          {error && <div className="text-xs text-red-300">{error}</div>}
        </div>
      </div>
    </div>
  );
}

export function SectionCard({ title, children, testid }) {
  return (
    <section data-testid={testid} className="rounded-2xl border border-warm bg-bg-card/30 backdrop-blur-xl p-8 space-y-5">
      <h3 className="font-serif-display text-2xl text-[color:var(--gold-hi)]">{title}</h3>
      {children}
    </section>
  );
}
